/**
 * Pure decision for the "your upload was auto-paused — resume?" popup
 * (bug 2026-08 follow-up).
 *
 * Called from `AutoPausedNoticeManager` on every packages-state change plus
 * on a low-cadence heartbeat. Returns the uploadId of an AUTO-paused upload
 * that deserves a popup RIGHT NOW, or null.
 *
 * Gates (both must be true):
 *   - The upload has been AUTO-paused for at least `minPausedMs` (default 60s).
 *     Gives the silent auto-resume path (AutoResumeWatcher) a chance to
 *     recover without ever bothering the user during transient dips.
 *   - No popup has been shown for this uploadId within `cooldownMs`
 *     (default 30 min). Prevents re-nag during genuinely flaky connections
 *     — user acknowledges once and isn't bombarded during the rest of the
 *     poor-network session.
 *
 * Deliberately returns AT MOST ONE uploadId so multiple simultaneously-
 * eligible uploads don't stack popup on popup.
 *
 * @param {Array<{uploadId: string, status: string, pauseReason?: string, pausedAt?: number}>} uploads
 * @param {object} options
 * @param {number} options.now                                      wall-clock ms
 * @param {Map<string, number>} options.lastShownAtByUploadId       uploadId → last-shown timestamp
 * @param {number} [options.minPausedMs=60000]
 * @param {number} [options.cooldownMs=1800000]                     30 min
 * @returns {string | null}
 */
export const decideShowAutoPausedPopup = (uploads, options = {}) => {
  const {
    now,
    lastShownAtByUploadId = new Map(),
    minPausedMs = 60_000,
    cooldownMs = 30 * 60 * 1000,
  } = options;
  if (!Array.isArray(uploads) || uploads.length === 0) return null;

  for (const upload of uploads) {
    if (!upload || upload.status !== "PAUSED") continue;
    if (upload.pauseReason !== "AUTO") continue;
    if (!upload.pausedAt) continue;
    if (now - upload.pausedAt < minPausedMs) continue;
    const lastShownAt = lastShownAtByUploadId.get(upload.uploadId) || 0;
    if (now - lastShownAt < cooldownMs) continue;
    return upload.uploadId;
  }
  return null;
};
